import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserHost } from '@/p2p/BrowserHost'
import { BrowserJoin } from '@/p2p/BrowserJoin'
import { OfflineHost } from '@/p2p/OfflineHost'
import { OfflineJoin } from '@/p2p/OfflineJoin'
import { Splash } from '@/p2p/Splash'
import './styles.css'

type Screen =
  | { kind: 'splash' }
  | { kind: 'browser-host' }
  | { kind: 'browser-join'; hostId: string }
  | { kind: 'offline-host' }
  | { kind: 'offline-join' }

function App() {
  const [screen, setScreen] = useState<Screen>(() => screenFromUrl())

  // Honour back/forward navigation: if the user pops to a URL with no
  // ?join= they should land back on the splash, not stay in a mode.
  useEffect(() => {
    const onPop = () => setScreen(screenFromUrl())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const exitToSplash = () => {
    const u = new URL(location.href)
    u.search = ''
    history.replaceState(null, '', u.toString())
    setScreen({ kind: 'splash' })
  }

  switch (screen.kind) {
    case 'splash':
      return (
        <Splash
          onPick={(m) => {
            if (m === 'browser-host') setScreen({ kind: 'browser-host' })
            else if (m === 'browser-join') {
              // Splash already wrote ?join= into the URL.
              const id = new URLSearchParams(location.search).get('join')
              if (id) setScreen({ kind: 'browser-join', hostId: id })
            } else if (m === 'offline-host') setScreen({ kind: 'offline-host' })
            else if (m === 'offline-join') setScreen({ kind: 'offline-join' })
          }}
        />
      )
    case 'browser-host':
      return <BrowserHost onExit={exitToSplash} />
    case 'browser-join':
      return <BrowserJoin hostId={screen.hostId} onExit={exitToSplash} />
    case 'offline-host':
      return <OfflineHost onExit={exitToSplash} />
    case 'offline-join':
      return <OfflineJoin onExit={exitToSplash} />
  }
}

function screenFromUrl(): Screen {
  const joinId = new URLSearchParams(location.search).get('join')
  if (joinId) return { kind: 'browser-join', hostId: joinId }
  return { kind: 'splash' }
}

const root = document.getElementById('root')!
ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
